/**
 * useFocusTrap - Hook for trapping focus within a container
 *
 * Essential for modal dialogs and dropdown menus to meet WCAG 2.1 requirements.
 *
 * @example
 * const containerRef = useFocusTrap(isOpen);
 * return <div ref={containerRef}>...</div>;
 */

import { useEffect, useRef, useCallback } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(", ");

export interface UseFocusTrapOptions {
  /** Whether the trap is active */
  enabled?: boolean;
  /** Return focus to trigger element on close */
  returnFocus?: boolean;
  /** Auto-focus first focusable element */
  autoFocus?: boolean;
  /** Element to focus initially (selector or element) */
  initialFocus?: string | HTMLElement | null;
}

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  options: UseFocusTrapOptions = {},
) {
  const {
    enabled = true,
    returnFocus = true,
    autoFocus = true,
    initialFocus,
  } = options;

  const containerRef = useRef<T>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Get all focusable elements within container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => el.offsetParent !== null); // Filter hidden elements
  }, []);

  // Handle Tab key navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled || e.key !== "Tab") return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    },
    [enabled, getFocusableElements],
  );

  useEffect(() => {
    if (!enabled) return;

    // Store current active element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Set initial focus
    if (autoFocus) {
      requestAnimationFrame(() => {
        if (!containerRef.current) return;

        let elementToFocus: HTMLElement | null = null;

        if (initialFocus) {
          if (typeof initialFocus === "string") {
            elementToFocus = containerRef.current.querySelector(initialFocus);
          } else {
            elementToFocus = initialFocus;
          }
        }

        if (!elementToFocus) {
          const focusable = getFocusableElements();
          elementToFocus = focusable[0] || containerRef.current;
        }

        elementToFocus?.focus();
      });
    }

    // Add keydown listener
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      // Return focus to previous element
      if (returnFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [
    enabled,
    autoFocus,
    initialFocus,
    returnFocus,
    getFocusableElements,
    handleKeyDown,
  ]);

  return containerRef;
}

export default useFocusTrap;
